import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {currenciesCache$} from '@/config';
import {indexPricesCache$} from '@/price-index/price-index';
import {fiatAccounts$} from '@/fiat-account/fiat-accounts';
import {fiat$} from '@/state/fiat';
import type {AssetEntity, CurrencyEntity, Assets, PricesCache, PriceEntity} from '@/types';
import FiatAccountRaw = fa.FiatAccountRaw;

export const fiatAccountTotalAssets$ = combineLatest([fiatAccounts$, indexPricesCache$, currenciesCache$, fiat$]).pipe(
  map(calcTotalAssets),
  filter<Assets>(Boolean)
);

function calcTotalAssets([fiatAccountAssets, indexPricesCache, currenciesCache, fiat]: [
  AssetEntity[],
  PricesCache,
  Map<string, CurrencyEntity>,
  string
]) {
  const btcCurrency = currenciesCache.get('BTC');
  const usdCurrency = currenciesCache.get('USD');
  const btcIndexPrice = indexPricesCache.get('.BTC');
  const usdtIndexPrice = indexPricesCache.get('.USDT');
  if (!btcCurrency || !usdCurrency || !btcIndexPrice || !usdtIndexPrice) {
    return null;
  }

  const [btcValuePrecision, btcValueScale, btcValueFactor] = convertCurrencyConfig(btcCurrency);
  const [usdValuePrecision, usdValueScale, usdValueFactor] = convertCurrencyConfig(usdCurrency);

  const usdEv = fiatAccountAssets.reduce((p, fiat: FiatAccountRaw) => p + calcUsdEv(fiat, indexPricesCache), 0);
  const btcEv = getBalanceEv(usdEv, btcIndexPrice.priceEp, usdValueFactor, 1e4, btcValueFactor);
  const fiatRate = indexPricesCache.get('.' + fiat);
  const moneyEv = calcMoneyEv(fiatRate, usdEv);
  const usdtEv = Big(usdEv).times(usdtIndexPrice.priceFactor).div(usdtIndexPrice.priceEp).toNumber();

  return {
    btcEv,
    btc: convertValue(btcEv, btcValuePrecision, btcValueScale),
    usdEv,
    usd: convertValue(usdEv, usdValuePrecision, usdValueScale),
    money: convertValue(moneyEv, usdValuePrecision, usdValueScale),
    usdtEv,
    usdt: convertValue(usdtEv, usdValuePrecision, usdValueScale),
  };
}

function convertCurrencyConfig(currencyInfo): [precision: number, scale: number, factor: number] {
  return [currencyInfo.valuePrecision, currencyInfo.valueScale, currencyInfo.valueFactor];
}

function getBalanceEv(valueEv, priceEp, valueFactor = 1e8, priceFactor = 1e4, balanceFactor = 1e8) {
  if (valueEv === 0) {
    return 0;
  }
  const factor = Big(balanceFactor).times(priceFactor).div(valueFactor);
  return Number(Big(valueEv).div(priceEp).times(factor));
}

function calcUsdEv(item, indexPricesCache): number {
  const {balanceEv, currency} = item;
  const rate = indexPricesCache.get('.' + currency);
  if (rate) {
    return (balanceEv * rate.priceEp) / 1e2;
  }
  return 0;
}

function calcMoneyEv(fiatRate: PriceEntity, usdEv) {
  if (!fiatRate) {
    return 0;
  }
  return Number(Big(usdEv).times(fiatRate.priceFactor).div(fiatRate.priceEp));
}

function convertValue(valueEv, precision, scale): string {
  return numUtil.delimit(numUtil.floor(valueEv, precision, scale));
}
